/**
 * AchievementGalleryRoute — Merker-fanen i Premier (Plan 02-14; #156, steg 6c,
 * Stell_Merker).
 *
 * Path: `/utmerkelser` (own viewer) and `/:memberId/utmerkelser` (admin/family
 * read of another member's gallery).
 *
 * Fra toppen på `/utmerkelser`:
 *   - PremierFrame: ScreenHeader «Premier» med saldopillen til Lommebok, og
 *     underfanene rett under (`drawsOwnHeader` / `ownTabs`);
 *   - «N av 12 merker»;
 *   - merkene i tre kolonner, hvert med skiven i en av tre tilstander
 *     (AchievementCell).
 *
 * `/:memberId/utmerkelser` ligger utenfor Premier-fanene og er ikke tegnet i
 * skissene. Der beholder skallet toppen sin, og skjermen viser bare telleren
 * og merkene — ingen underfaner og ingen saldo som ikke er den innloggedes.
 *
 * Utelatt fra skissen: «Neste: En hel uke» og fremdrift per merke («18 av 25»)
 * trenger data som ikke finnes (`AchievementView` har ingen fremdrift).
 *
 * `?focus=<key>` kommer fra push-varselet om et nytt merke
 * (achievements.subscribers.ts). Merket får en ring og rulles fram, som
 * `?id=` på Godkjenn.
 *
 * Tap routing (driven by AchievementCell):
 *   - unlocked          → openSheet(<this entry>)
 *   - locked-non-secret → toast (handled inside AchievementCell)
 *   - locked-secret     → toast (handled inside AchievementCell)
 *
 * Viewer identity: when a `memberId` prop is passed, the route fetches that
 * member's gallery; otherwise the URL's `:memberId`, and then the viewer from
 * the shared session (`useSession`, /api/auth/me). The
 * App.tsx route mounts the prop-less variant; tests pass the prop directly
 * for deterministic mock setup.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { EmptyState, ErrorState, LoadingState } from '../../components/primitives';
import { useSession } from '../auth/useSession';
import { drawsOwnHeader } from '../dashboard/navTabs';
import { useBalance } from '../points/usePointsQueries';
import { BalancePill, PREMIER_MAIN, PremierFrame } from '../rewards/PremierFrame';
import { AchievementCell } from './AchievementCell';
import { AchievementSheet } from './AchievementSheet';
import { useAchievements, type AchievementView } from './useAchievementsQuery';

interface AchievementGalleryRouteProps {
  /** Optional override — when omitted, the viewer's own id from /api/auth/me. */
  memberId?: string;
}

// Synkront, som i PointBalance: motion sin useReducedMotion() er null i første
// render, og rullingen skjer nettopp i den første renderen med data.
function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Rammen: Premier-toppen med saldo og underfaner på `/utmerkelser`, og en
 * enkel flate på `/:memberId/utmerkelser`, der skallet tegner toppen.
 */
function GalleryFrame({
  testId,
  memberId,
  premier,
  children,
}: {
  testId: string;
  memberId: string | null;
  premier: boolean;
  children: ReactNode;
}): ReactNode {
  const { data: balance } = useBalance(memberId ?? '', premier && !!memberId);
  if (!premier) {
    return (
      <main data-testid={testId} className={PREMIER_MAIN}>
        {children}
      </main>
    );
  }
  return (
    <PremierFrame
      testId={testId}
      trailing={memberId ? <BalancePill balance={balance} data-testid="achievement-gallery-balance" /> : undefined}
    >
      {children}
    </PremierFrame>
  );
}

export function AchievementGalleryRoute({
  memberId: memberIdProp,
}: AchievementGalleryRouteProps = {}): ReactNode {
  const { t } = useTranslation();
  const location = useLocation();
  // Samme liste som skallet bruker, så toppen aldri tegnes to ganger eller
  // ikke i det hele tatt.
  const premier = drawsOwnHeader(location.pathname);
  const params = useParams();
  const session = useSession();
  const memberId = memberIdProp ?? params.memberId ?? session.data?.member.id ?? null;
  const query = useAchievements(memberId ?? '', Boolean(memberId));
  const [sheetEntry, setSheetEntry] = useState<AchievementView | null>(null);

  const rows = query.data?.achievements;
  // Bare en nøkkel som finnes i galleriet tas i bruk, så søkestrengen aldri
  // havner rett i en selektor.
  const focusParam = useSearchParams()[0].get('focus');
  const focusKey = rows?.some((r) => r.key === focusParam) ? focusParam : null;

  useEffect(() => {
    if (!focusKey) return;
    const el = document.querySelector(`[data-testid="achievement-cell-${focusKey}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }
    // location.key: en ny push-lenke til samme merke ruller fram igjen.
  }, [focusKey, location.key]);

  // Uten økt (kjøkkenskjermen kan stå uten, DASH-06) eller med en henting som
  // feilet, blir ikke skjelettet stående.
  if (!memberId && !session.isPending) {
    return (
      <GalleryFrame testId="achievement-gallery-403" memberId={null} premier={premier}>
        <p className="m-0 py-8 text-center text-sm text-ink-2">{t('achievements.notAuthFallback')}</p>
      </GalleryFrame>
    );
  }

  if (query.isError) {
    return (
      <GalleryFrame testId="achievement-gallery-error" memberId={memberId} premier={premier}>
        <ErrorState
          variant="inline"
          heading={t('errors.generic.heading')}
          body={t('errors.generic.body')}
          onRetry={() => void query.refetch()}
        />
      </GalleryFrame>
    );
  }

  // Plan 06-02 Task 2: replace the empty `<main aria-hidden="true" />`
  // placeholder with a warm-skeleton <LoadingState> so the gallery
  // surface communicates layout shape during /api/achievements fetch.
  if (!memberId || !rows) {
    return (
      <GalleryFrame testId="achievement-gallery-loading" memberId={memberId} premier={premier}>
        <LoadingState lines={4} />
      </GalleryFrame>
    );
  }

  const Heading = premier ? 'h2' : 'h1';
  const unlockedCount = rows.filter((r) => r.unlocked).length;
  const total = rows.length;

  return (
    <GalleryFrame testId="achievement-gallery" memberId={memberId} premier={premier}>
      {/* Plan 06-02 Task 2: when the API returns an empty achievements
          list (defensive — a freshly-seeded household ships with all
          locked entries pre-populated), render the Phase-6 <EmptyState>
          instead of an invisible empty grid. Uses
          achievements.empty.{heading,body} per UI-SPEC §Copywriting. */}
      {rows.length === 0 ? (
        <div data-testid="achievement-gallery-empty">
          <EmptyState
            variant="inline"
            heading={t('achievements.empty.heading')}
            body={t('achievements.empty.body')}
          />
        </div>
      ) : (
        <>
          {/* «Premier» er h1 i toppen og telleren h2, som i skissen. Uten
              Premier-toppen er telleren sidens h1. */}
          <Heading
            data-testid="achievement-gallery-heading"
            className="tnum m-0 mt-0.5 text-base font-extrabold text-ink"
          >
            {t('achievements.gallery.count', { unlocked: unlockedCount, total })}
          </Heading>
          <ul
            data-testid="achievement-gallery-grid"
            className="m-0 grid list-none grid-cols-3 gap-2 p-0 md:grid-cols-4 lg:grid-cols-6"
          >
            {rows.map((entry) => (
              <li key={entry.key}>
                <AchievementCell
                  achievement={entry}
                  focused={entry.key === focusKey}
                  onClick={() => setSheetEntry(entry)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      <AchievementSheet
        open={sheetEntry !== null}
        onClose={() => setSheetEntry(null)}
        achievement={sheetEntry}
      />
    </GalleryFrame>
  );
}
